import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import {
  PowerSyncContext,
  usePowerSync as useBasePowerSync,
} from '@powersync/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createPowerSync } from '@/lib/powersync/db';
import { AbstractPowerSyncDatabase } from '@powersync/react-native';
import type { Database } from '../../generated/schema';
import { logger } from '@/lib/logger';
import { PowerSyncKyselyDatabase } from '@powersync/kysely-driver';
import { useAuth } from '~/auth/contexts/auth/Auth';
import { ApolloPowerSyncConnector } from '@/lib/powersync/connector';
import type { QueryBuilderSpec } from '../../types';
import { WatchQueryCache } from './watch-query-cache';

interface WatchQueryContextType {
  subscribeToQuery: <T>(
    queryKey: string,
    querySpec: QueryBuilderSpec<T>,
    listener: (data: T[]) => void,
  ) => () => void;
}

const WatchQueryContext = createContext<WatchQueryContextType | null>(null);

export const useWatchQueryCache = () => {
  const context = useContext(WatchQueryContext);
  if (!context) {
    throw new Error('useWatchQueryCache must be used within PowerSyncProvider');
  }
  return context;
};

// Create a single QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - PowerSync data doesn't change frequently
    },
  },
});

export interface PowerSyncProviderProps {
  children?: ReactNode;
}

export const PowerSyncProvider = ({ children }: PowerSyncProviderProps) => {
  const [powerSync, setPowerSync] = useState<AbstractPowerSyncDatabase | null>(
    null,
  );
  const [kyselyDb, setKyselyDb] = useState<AbstractPowerSyncDatabase | null>(
    null,
  );
  const initialized = useRef(false);
  const { auth } = useAuth();
  const connectorRef = useRef<ApolloPowerSyncConnector | null>(null);

  // One shared live watch per query key, started under an AbortController so the
  // underlying PowerSync watch is torn down when its last subscriber unmounts
  // (previously the callback watch had a no-op cleanup and leaked for the whole
  // session). Recreated if the db instance swaps.
  const watchQueryCache = useMemo(() => {
    if (!kyselyDb) return null;
    const typedDb = kyselyDb as unknown as PowerSyncKyselyDatabase<Database>;
    return new WatchQueryCache<QueryBuilderSpec<unknown>>(
      (queryKey, querySpec, onResult, signal) => {
        const query = querySpec.createQuery?.(typedDb);
        if (!query) {
          logger.warn(`Query builder is null for ${queryKey}`);
          return;
        }
        // Passing `signal` lets `cache.subscribe`'s teardown abort the watch.
        typedDb.watch(query, { onResult }, { signal });
      },
    );
  }, [kyselyDb]);

  const subscribeToQuery = useCallback(
    <T,>(
      queryKey: string,
      querySpec: QueryBuilderSpec<T>,
      listener: (data: T[]) => void,
    ) => {
      if (!watchQueryCache) {
        logger.warn('Cannot subscribe - PowerSync not initialized');
        return () => {};
      }
      return watchQueryCache.subscribe(
        queryKey,
        querySpec as QueryBuilderSpec<unknown>,
        listener,
      );
    },
    [watchQueryCache],
  );

  // Initialize PowerSync instance once
  useEffect(() => {
    if (initialized.current) {
      logger.debug('PowerSync already initialized, skipping');
      return;
    }

    // Initialize PowerSync once on app startup
    logger.debug('Starting PowerSync initialization');
    initialized.current = true;

    createPowerSync()
      .then(({ powerSyncDb, db }) => {
        logger.debug('PowerSync initialization successful, setting instances');
        setPowerSync(powerSyncDb);
        setKyselyDb(
          Object.assign(db, {
            base: powerSyncDb,
          }) as unknown as AbstractPowerSyncDatabase,
        );
      })
      .catch((error) => {
        logger.error('Failed to initialize PowerSync:', error);
        initialized.current = false; // Allow retry on error
      });
  }, []);

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (!powerSync) return;

    if (auth) {
      logger.debug('User authenticated, connecting PowerSync');
      if (!connectorRef.current) {
        connectorRef.current = new ApolloPowerSyncConnector();
      }
      powerSync.connect(connectorRef.current).catch((error: Error) => {
        logger.error('Failed to connect PowerSync:', error);
      });
    } else {
      logger.debug('User not authenticated, clearing PowerSync data');
      powerSync.disconnectAndClear().catch((error: Error) => {
        logger.error('Failed to disconnect and clear PowerSync:', error);
      });
      // Clean up connector reference on logout
      connectorRef.current = null;
    }
  }, [auth, powerSync]);

  if (!kyselyDb) {
    return children;
  }
  return (
    <QueryClientProvider client={queryClient}>
      <PowerSyncContext.Provider value={kyselyDb}>
        <WatchQueryContext.Provider value={{ subscribeToQuery }}>
          {children}
        </WatchQueryContext.Provider>
      </PowerSyncContext.Provider>
    </QueryClientProvider>
  );
};

export type PowerSyncDb = PowerSyncKyselyDatabase<Database> & {
  db: AbstractPowerSyncDatabase;
};

export const usePowerSync = useBasePowerSync as unknown as () => PowerSyncDb;
